import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Search, Calendar, ArrowLeft, Package, Filter, X } from 'lucide-react';
import { encryptId } from '../utils/urlEncryption';
import { toast } from 'sonner';

const ItemTracker = ({ user }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState({});
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [summary, setSummary] = useState({ item_type_summary: {}, source_type_summary: {} });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [itemsRes, customersRes] = await Promise.all([
        axiosInstance.get('/reports/item-tracker'),
        axiosInstance.get('/customers', { params: { limit: 1000 } })
      ]);

      // Build customer lookup map
      const custMap = {};
      (customersRes.data || []).forEach(c => {
        if (c.customer_id) custMap[c.customer_id] = c;
        if (c.id) custMap[c.id] = c;
      });
      setCustomers(custMap);

      const data = itemsRes.data;
      setItems(data.items || []);
      setSummary({
        item_type_summary: data.item_type_summary || {},
        source_type_summary: data.source_type_summary || {},
      });
    } catch (error) {
      console.error('Error loading items:', error);
      toast.error('Failed to load item tracker data');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = useMemo(() => {
    let result = [...items];

    if (sourceFilter !== 'All') {
      result = result.filter(i => i.source_type === sourceFilter);
    }
    if (typeFilter !== 'All') {
      result = result.filter(i => i.item_type === typeFilter);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(i => {
        const cust = customers[i.customer_id];
        const custName = cust?.name?.toLowerCase() || '';
        const custMobile = cust?.mobile || cust?.phone || '';
        return (
          i.item_type?.toLowerCase().includes(q) ||
          i.purity?.toLowerCase().includes(q) ||
          i.source_id?.toLowerCase().includes(q) ||
          i.item_id?.toLowerCase().includes(q) ||
          i.vendor_short_code?.toLowerCase().includes(q) ||
          i.tag_number?.toLowerCase().includes(q) ||
          custName.includes(q) ||
          custMobile.includes(q)
        );
      });
    }

    return result;
  }, [items, searchTerm, sourceFilter, typeFilter, customers]);

  const itemTypes = useMemo(() => {
    return Object.keys(summary.item_type_summary).sort();
  }, [summary]);

  const navigateToSource = (item) => {
    const { source_type, source_id } = item;
    if (!source_id) return;
    const encryptedId = encryptId(source_id);

    if (source_type === 'Order') navigate(`/orders/${encryptedId}`);
    else if (source_type === 'Booking') navigate(`/bookings/${encryptedId}`);
    else if (source_type === 'Approval') navigate(`/approvals/${encryptedId}`);
    else if (source_type === 'Selection') navigate(`/selections/${encryptedId}`);
  };

  const getWeight = (item) => {
    if (item.weight_fixed) return `${item.weight_fixed}g`;
    if (item.weight_min && item.weight_max) return `${item.weight_min}-${item.weight_max}g`;
    if (item.weight_min) return `${item.weight_min}g`;
    return '-';
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-700';
    const s = status.toLowerCase();
    if (s === 'completed' || s === 'delivered') return 'bg-green-100 text-green-800';
    if (s === 'active' || s === 'in progress') return 'bg-blue-100 text-blue-800';
    if (s === 'cancelled' || s === 'rejected') return 'bg-red-100 text-red-800';
    return 'bg-amber-100 text-amber-800';
  };

  const getSourceColor = (type) => {
    if (type === 'Order') return 'bg-blue-100 text-blue-800';
    if (type === 'Booking') return 'bg-purple-100 text-purple-800';
    if (type === 'Approval') return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSourceFilter('All');
    setTypeFilter('All');
  };

  const hasActiveFilters = searchTerm || sourceFilter !== 'All' || typeFilter !== 'All';

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-amber-500 text-white px-4 sm:px-6 py-4 sm:py-6 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Item Tracker</h1>
              <p className="text-orange-100 text-xs sm:text-sm">Track items across orders, bookings & approvals</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          {['Order', 'Booking', 'Approval'].map(type => (
            <button
              key={type}
              onClick={() => setSourceFilter(sourceFilter === type ? 'All' : type)}
              className={`rounded-xl p-3 sm:p-4 text-center transition-all border ${
                sourceFilter === type
                  ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200'
                  : 'border-gray-200 bg-white hover:border-orange-300'
              }`}
              data-testid={`filter-${type.toLowerCase()}`}
            >
              <div className="text-lg sm:text-2xl font-bold text-gray-900">
                {summary.source_type_summary[type] || 0}
              </div>
              <div className="text-[10px] sm:text-xs text-gray-500 font-medium">{type}s</div>
            </button>
          ))}
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search item type, customer, ID..."
                className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                data-testid="search-input"
              />
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="text-xs sm:text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white min-w-[120px]"
              data-testid="type-filter"
            >
              <option value="All">All Types</option>
              {itemTypes.map(type => (
                <option key={type} value={type}>{type} ({summary.item_type_summary[type]})</option>
              ))}
            </select>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>{filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''} found</span>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-orange-600 hover:text-orange-800 text-xs font-medium">
              Clear all filters
            </button>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto"></div>
            <p className="mt-3 text-gray-500 text-sm">Loading items...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredItems.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-700 mb-1">No items found</h3>
            <p className="text-gray-500 text-sm">Try adjusting your search or filters</p>
          </div>
        )}

        {/* Desktop Table */}
        {!loading && filteredItems.length > 0 && (
          <div className="hidden lg:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="items-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Image</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Item</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Weight</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredItems.map((item, idx) => {
                    const cust = customers[item.customer_id];
                    return (
                      <tr
                        key={`${item.source_id}-${item.item_id}-${idx}`}
                        onClick={() => navigateToSource(item)}
                        className="hover:bg-orange-50 cursor-pointer transition-colors"
                        data-testid={`item-row-${idx}`}
                      >
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getSourceColor(item.source_type)}`}>
                            {item.source_type}
                          </span>
                          <div className="text-xs text-gray-500 mt-1 font-mono">{item.source_id}</div>
                        </td>
                        <td className="px-4 py-3">
                          {item.photos && item.photos.length > 0 ? (
                            <img
                              src={item.photos[0]}
                              alt={item.item_type}
                              className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                              <Package className="w-5 h-5 text-gray-300" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(item.created_at)}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{cust?.name || '-'}</div>
                          <div className="text-xs text-gray-500">{cust?.mobile || cust?.phone || ''}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{item.item_type}</div>
                          <div className="text-xs text-gray-500">
                            {[item.purity, item.finish_color, item.size ? `Size ${item.size}` : ''].filter(Boolean).join(' | ')}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{getWeight(item)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.vendor_short_code || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${getStatusColor(item.status)}`}>
                            {item.status || '-'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Mobile Card View */}
        {!loading && filteredItems.length > 0 && (
          <div className="lg:hidden space-y-2" data-testid="items-mobile-list">
            {filteredItems.map((item, idx) => {
              const cust = customers[item.customer_id];
              return (
                <div
                  key={`${item.source_id}-${item.item_id}-${idx}`}
                  onClick={() => navigateToSource(item)}
                  className="bg-white rounded-xl border border-gray-200 p-3 active:bg-orange-50 transition-colors cursor-pointer"
                  data-testid={`item-card-${idx}`}
                >
                  <div className="flex gap-3">
                    {/* Thumbnail */}
                    <div className="flex-shrink-0">
                      {item.photos && item.photos.length > 0 ? (
                        <img
                          src={item.photos[0]}
                          alt={item.item_type}
                          className="w-14 h-14 rounded-lg object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center">
                          <Package className="w-6 h-6 text-gray-300" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${getSourceColor(item.source_type)}`}>
                            {item.source_type}
                          </span>
                          <span className="text-xs text-gray-500 font-mono truncate">{item.source_id}</span>
                        </div>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${getStatusColor(item.status)}`}>
                          {item.status || '-'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-gray-900">{item.item_type}</span>
                        <span className="text-sm font-bold text-gray-900">{getWeight(item)}</span>
                      </div>

                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{cust?.name || '-'}</span>
                        <span>{formatDate(item.created_at)}</span>
                      </div>

                      {(item.purity || item.vendor_short_code) && (
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                          {item.purity && <span>{item.purity}</span>}
                          {item.finish_color && <span>| {item.finish_color}</span>}
                          {item.vendor_short_code && <span>| {item.vendor_short_code}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemTracker;
